import type {
  CustomerPortalMenuItem,
  CustomerPortalMenuTarget,
} from '@/types/customer-portal-menu';

const MAX_ORDER = 10000;

const normalizeString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeOrder = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const intValue = Math.trunc(value);
    if (intValue < 0 || intValue > MAX_ORDER) {
      return null;
    }
    return intValue;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      return null;
    }
    const intValue = Math.trunc(parsed);
    if (intValue < 0 || intValue > MAX_ORDER) {
      return null;
    }
    return intValue;
  }

  return null;
};

export const isValidHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return (url.protocol === 'http:' || url.protocol === 'https:') && Boolean(url.host);
  } catch {
    return false;
  }
};

export const normalizePortalMenuTarget = (
  value: unknown,
  fallback: CustomerPortalMenuTarget = '_blank',
): CustomerPortalMenuTarget => {
  if (value === '_self' || value === '_blank') {
    return value;
  }
  return fallback;
};

export const normalizePortalMenuItem = (value: unknown): CustomerPortalMenuItem | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  const label = normalizeString(record.label);
  const url = normalizeString(record.url);

  if (!label || !url || !isValidHttpUrl(url)) {
    return null;
  }

  const icon = normalizeString(record.icon);
  const order = normalizeOrder(record.order);
  const target = normalizePortalMenuTarget(record.target);

  return {
    label,
    url,
    icon,
    order,
    target,
  };
};

export const normalizePortalMenuItems = (value: unknown): CustomerPortalMenuItem[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => normalizePortalMenuItem(item))
    .filter((item): item is CustomerPortalMenuItem => Boolean(item));
};
